import React, { useState } from 'react';
import LinkButtonWithIcon from '../../../../Components/Button/LinkButtonWithIcon';
import { Position } from '../../../Base/Enums/Position.enum';
import styles from './DENTModalOverviewGeneralButtons.module.scss';
import DENTConfirmationModal from '../DENTToothDetailModal/DENTConfirmationModal';
import { ToothDetailContentType } from '../../Enums/ToothDetailContentType.enum';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import { useDENTContext } from '../../Services/DENTContext';
import { ToothState, useResetToothMutation } from '../../../../graphql/graphql';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';

interface Props {
	onChangeContentType: (type: ToothDetailContentType) => void;
	onClose: () => void;
}

interface ConfirmationModal {
	title: string;
	text: string;
	onSubmit: () => void;
}

enum ConfirmationModalType {
	RESET = 'RESET',
	EXTRACTION = 'EXTRACTION',
	MISSING = 'MISSING',
	NOT_MISSING = 'NOT_MISSING',
	CANCEL_EXTRACTION = 'CANCEL_EXTRACTION',
}

const DENTModalOverviewGeneralButtons = ({
	onChangeContentType,
	onClose,
}: Props) => {
	const {
		activeTooth,
		handleUpdateTooth,
		reloadMouthData,
		activeMedicalExaminationId,
	} = useDENTContext();
	const [resetTooth] = useResetToothMutation();
	const { displayGQLError } = useApiClient();
	const { showNotificationSuccess } = useNotification();

	const [confirmationModalTypeOpened, setConfirmationModalTypeOpened] =
		useState<ConfirmationModalType | null>(null);

	const handleReset = async () => {
		if (!(activeTooth && activeMedicalExaminationId)) return;

		const response = await resetTooth({
			where: { id: activeTooth.id },
			input: { medicalExaminationId: activeMedicalExaminationId },
		});

		if ('error' in response) {
			displayGQLError(response);
		} else {
			showNotificationSuccess({
				content: 'patientTreatment.meddiDENT.toothUpdated',
			});
			reloadMouthData();
		}
	};

	const handlePathology = () => {
		setConfirmationModalTypeOpened(null);
		onChangeContentType(ToothDetailContentType.PATHOLOGY);
	};

	const handleRestoration = () => {
		setConfirmationModalTypeOpened(null);
		onChangeContentType(ToothDetailContentType.RESTORATION);
	};

	const CONFIRMATION_MODALS: Record<ConfirmationModalType, ConfirmationModal> =
		{
			[ConfirmationModalType.RESET]: {
				title:
					'patientTreatment.meddiDENT.dentalCross.overviewModal.resetModalTitle',
				text: 'patientTreatment.meddiDENT.dentalCross.overviewModal.resetModalText',
				onSubmit: handleReset,
			},
			[ConfirmationModalType.EXTRACTION]: {
				title:
					'patientTreatment.meddiDENT.dentalCross.overviewModal.extractionModalTitle',
				text: 'patientTreatment.meddiDENT.dentalCross.overviewModal.extractionModalText',
				onSubmit: () => handleUpdateTooth({ toBeExtracted: true }),
			},
			[ConfirmationModalType.MISSING]: {
				title:
					'patientTreatment.meddiDENT.dentalCross.overviewModal.missingModalTitle',
				text: 'patientTreatment.meddiDENT.dentalCross.overviewModal.missingModalText',
				onSubmit: () => handleUpdateTooth({ missing: true }),
			},
			[ConfirmationModalType.NOT_MISSING]: {
				title:
					'patientTreatment.meddiDENT.dentalCross.overviewModal.notMissingModalTitle',
				text: 'patientTreatment.meddiDENT.dentalCross.overviewModal.notMissingModalText',
				onSubmit: () => handleUpdateTooth({ missing: false }),
			},
			[ConfirmationModalType.CANCEL_EXTRACTION]: {
				title:
					'patientTreatment.meddiDENT.dentalCross.overviewModal.cancelExtractionModalTitle',
				text: 'patientTreatment.meddiDENT.dentalCross.overviewModal.cancelExtractionModalText',
				onSubmit: () => handleUpdateTooth({ toBeExtracted: false }),
			},
		};

	return (
		<div className={styles.container}>
			<LinkButtonWithIcon
				onClick={() =>
					setConfirmationModalTypeOpened(ConfirmationModalType.RESET)
				}
				title={'patientTreatment.meddiDENT.dentalCross.overviewModal.reset'}
				icon={'/dist/icons/dent/reset.svg'}
				iconPosition={Position.LEFT}
			/>
			{activeTooth?.state === ToothState.Missing && (
				<LinkButtonWithIcon
					onClick={() =>
						setConfirmationModalTypeOpened(ConfirmationModalType.NOT_MISSING)
					}
					title={
						'patientTreatment.meddiDENT.dentalCross.overviewModal.notMissing'
					}
					icon={'/dist/icons/dent/x.svg'}
					iconPosition={Position.LEFT}
				/>
			)}
			{activeTooth?.state !== ToothState.Missing && (
				<>
					{activeTooth?.state === ToothState.ToBeExtracted ? (
						<LinkButtonWithIcon
							onClick={() =>
								setConfirmationModalTypeOpened(
									ConfirmationModalType.CANCEL_EXTRACTION,
								)
							}
							title={
								'patientTreatment.meddiDENT.dentalCross.overviewModal.cancelExtraction'
							}
							icon={'/dist/icons/dent/hourglass.svg'}
							iconPosition={Position.LEFT}
						/>
					) : (
						<LinkButtonWithIcon
							onClick={() =>
								setConfirmationModalTypeOpened(ConfirmationModalType.EXTRACTION)
							}
							title={
								'patientTreatment.meddiDENT.dentalCross.overviewModal.extraction'
							}
							icon={'/dist/icons/dent/hourglass.svg'}
							iconPosition={Position.LEFT}
						/>
					)}
					<LinkButtonWithIcon
						onClick={() =>
							setConfirmationModalTypeOpened(ConfirmationModalType.MISSING)
						}
						title={
							'patientTreatment.meddiDENT.dentalCross.overviewModal.missing'
						}
						icon={'/dist/icons/dent/x.svg'}
						iconPosition={Position.LEFT}
					/>
					<LinkButtonWithIcon
						onClick={handlePathology}
						title={
							'patientTreatment.meddiDENT.dentalCross.overviewModal.pathology'
						}
						icon={'/dist/icons/dent/plus.svg'}
						iconPosition={Position.LEFT}
					/>
				</>
			)}
			<LinkButtonWithIcon
				onClick={handleRestoration}
				title={
					'patientTreatment.meddiDENT.dentalCross.overviewModal.restoration'
				}
				icon={'/dist/icons/dent/plus.svg'}
				iconPosition={Position.LEFT}
			/>
			<LinkButton
				title='base.shared.close'
				theme={Theme.RED}
				onClick={onClose}
			/>

			{confirmationModalTypeOpened && (
				<DENTConfirmationModal
					isOpen
					onClose={() => setConfirmationModalTypeOpened(null)}
					onConfirm={CONFIRMATION_MODALS[confirmationModalTypeOpened].onSubmit}
					title={CONFIRMATION_MODALS[confirmationModalTypeOpened].title}
					text={CONFIRMATION_MODALS[confirmationModalTypeOpened].text}
				/>
			)}
		</div>
	);
};

export default DENTModalOverviewGeneralButtons;
